import type { Dispatch, SetStateAction } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { updateFeedbackBodyZod } from "@nlc/shared-types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { CheckCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { UpdateFeedbackFormData } from "@/features/request/feedback-tool/feedbackTool/hooks/useUpdateFeedback.tsx";
import type { Feedback } from "@/features/request/feedback-tool/feedbackTool/types";

import { Button } from "@/components/button";
import { Text } from "@/components/text";
import { toast } from "@/components/toast/toast-queue.ts";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import Asset from "@/features/request/feedback-tool/feedbackTool/components/Asset.tsx";
import { DeleteDialog } from "@/features/request/feedback-tool/feedbackTool/components/DeleteDialog.tsx";
import UploadAsset from "@/features/request/feedback-tool/feedbackTool/components/UploadAsset.tsx";
import { useDeleteFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useDeleteFeedback.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useResolveFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useResolveFeedback.tsx";
import { useUpdateFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useUpdateFeedback.tsx";

import { useLoadingMutation } from "../hooks/useLoadingMutation";
import "../styles/form.css";
import { Loading } from "./Loading";
import { TextareaEditor } from "./TextareaComment";

dayjs.extend(relativeTime);

export function FeedbackList({
  feedBacks,
  formFeedback,
  selected,
  setFormFeedBack,
  setSelected,
  size,
}: {
  feedBacks: Array<Feedback> | null;
  formFeedback:
    | ({ type: "ADD" | "EDIT" | "VIEW" | undefined } & Feedback)
    | null;
  selected: Feedback | null;
  setFormFeedBack: Dispatch<
    SetStateAction<
      ({ type: "ADD" | "EDIT" | "VIEW" | undefined } & Feedback) | null
    >
  >;
  setSelected: Dispatch<SetStateAction<Feedback | null>>;
  size: "FULL" | "MODAL";
}) {
  const { hasRole } = useRoleAccess();
  const isDesignerOnly = hasRole(["designer"]);
  const canResolve = hasRole(["designer", "team-leader", "super-admin"]);
  const { img } = useFeedbackTool();
  const feedbackRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState("");
  const shouldDisableEditing = feedBacks?.some((item) => item.isResolved);

  const setFeedbackRef = (id: string, element: HTMLDivElement | null) => {
    if (element) {
      feedbackRefs.current.set(id, element);
    } else {
      feedbackRefs.current.delete(id);
    }
  };

  useEffect(() => {
    if (selected?.id) {
      const selectedRef = feedbackRefs.current.get(selected.id.toString());

      setUpdateError("");

      if (selectedRef) {
        setTimeout(() => {
          selectedRef.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, [selected]);

  const { mutate } = useUpdateFeedback(selected?.storyId, img.id);
  const { mutate: deleteFeedback } = useDeleteFeedback(
    selected?.commentBatchId,
    img.id,
  );
  const { mutate: resolveFeedback } = useResolveFeedback(
    selected?.commentBatchId,
    img.id,
  );

  const isUpdateLoading = useLoadingMutation("UPDATE", img.id);
  const isResolveLoading = useLoadingMutation("RESOLVE", img.id);

  const { control, getValues, reset } = useForm<UpdateFeedbackFormData>({
    defaultValues: {
      comment: "",
    },
    resolver: zodResolver(updateFeedbackBodyZod),
  });

  useEffect(() => {
    if (selected) {
      reset({
        comment: selected.description ?? "",
      });
    }
  }, [selected]);

  const canResolveAsDesigner = (isResolved: boolean | undefined) =>
    canResolve && !isResolved;
  const canEdit = (isSelected: boolean) =>
    !isDesignerOnly && formFeedback?.type === "EDIT" && isSelected;

  return (
    <div
      className={`overflow-auto w-full ${size === "MODAL" ? "h-150" : "h-full sm:max-h-full"}`}
    >
      {feedBacks?.map((item: Feedback) => {
        const isSelected = item.id === selected?.id;

        return (
          <div
            className="flex w-full pt-6 pb-6 sm:p-6 items-start"
            key={item.id}
            ref={(el) => setFeedbackRef(item.id.toString(), el)}
          >
            <Button
              className={`
                pr-4 h-7 w-6 rounded-full flex items-center justify-center cursor-pointer relative top-1 
                ${
                  item.id === selected?.id
                    ? item.isResolved
                      ? "bg-utility-success-600 hover:bg-utility-success-600 transition-colors duration-200"
                      : "bg-button-primary-bg"
                    : item.isResolved
                      ? "bg-utility-success-600 opacity-50"
                      : "bg-gray-500 text-text-primary opacity-50"
                }
                } text-white z-2
                `}
              onPress={() => {
                if (item === selected) {
                  setSelected(null);
                } else {
                  setSelected(item);
                }
              }}
            >
              {item.id}
            </Button>
            <div
              className="flex flex-col justify-start text-left pl-3 w-full cursor-pointer"
              onClick={() => {
                if (window.getSelection()?.toString()) {
                  return;
                }

                if (formFeedback?.type === "EDIT") {
                  return;
                }

                if (item.id === selected?.id) {
                  return setSelected(null);
                }

                setSelected(item);
              }}
            >
              <strong
                className={`${isSelected ? "text-text-secondary" : "text-gray-500"} text-md`}
              >
                {item.user.name}
              </strong>
              <Text
                className={`${isSelected ? "text-text-secondary" : "text-gray-500"} font-normal text-sm`}
              >
                {dayjs(item.updatedAt).fromNow()}
              </Text>
              {formFeedback?.type === "EDIT" && isSelected ? (
                <Controller
                  control={control}
                  name="comment"
                  render={({ field: { name, onChange, value } }) => (
                    <div className="pl-0">
                      <TextareaEditor
                        className="w-[238px]"
                        name={name}
                        onChange={onChange}
                        value={value}
                      />
                      <span className="block text-sm font-normal text-text-error-primary mt-sm">
                        {updateError}
                      </span>
                    </div>
                  )}
                />
              ) : (
                <>
                  {item.id === selected?.id && showMore ? (
                    <div
                      className={`rendered-html cursor-pointer list-inside resize-none overflow-hidden h-auto ${isSelected ? "text-text-primary" : "text-gray-500"} mb-2 border border-transparent text-base pt-4 list-disc`}
                      dangerouslySetInnerHTML={{
                        __html: item?.description ?? "",
                      }}
                    />
                  ) : (
                    <div
                      className={`rendered-html list-inside resize-none overflow-hidden h-auto ${isSelected ? "text-text-primary" : "text-gray-500"} mb-2 border border-transparent text-base pt-4 list-disc`}
                      dangerouslySetInnerHTML={{
                        __html:
                          item.description && item.description.length > 90
                            ? `${item.description.slice(0, 90)}...`
                            : (item.description ?? ""),
                      }}
                    />
                  )}
                  {item.description && item.description?.length > 90 ? (
                    <Button
                      className={`justify-end pt-0 text-text-secondary ${item.id === selected?.id ? "" : "opacity-50"}`}
                      onPress={() => {
                        if (item.id === selected?.id) {
                          return setShowMore(!showMore);
                        }

                        setShowMore(!showMore);
                        setSelected(item);
                      }}
                      variant="plain"
                    >
                      {item.id === selected?.id && showMore
                        ? "see less"
                        : "see more"}
                    </Button>
                  ) : null}
                </>
              )}
              {item?.attachments?.map((attachment) => (
                <Asset
                  attachment={attachment}
                  isActive={isSelected}
                  isSelected={isSelected}
                  key={attachment.attachmentId}
                />
              ))}
              <UploadAsset
                commentBatchId={item.commentBatchId ?? ""}
                isSelected={isSelected}
                key={item.attachments?.length}
                parentId={item.parentId ?? 0}
              />
              {item.isResolved ? (
                <Button
                  className={`text-utility-success-600 mt-6 border-utility-success-600 ${isSelected ? "" : "opacity-50"}`}
                  variant="secondary"
                >
                  <CheckCircle />
                  Resolved
                </Button>
              ) : null}
              {canResolveAsDesigner(item.isResolved) ? (
                <Button
                  className={`mt-6 border-utility-success-600 cursor-pointer ${isSelected ? "" : "opacity-50"}`}
                  isDisabled={!!isResolveLoading || !isSelected}
                  onPress={() => resolveFeedback()}
                  variant="primary"
                >
                  {isResolveLoading ? <Loading /> : "Resolve"}
                </Button>
              ) : null}
              {canEdit(isSelected) && !item.isResolved ? (
                <div className="flex w-full gap-2 mt-3">
                  <Button
                    className={`${isSelected ? "text-gray-700 border-gray-400 cursor-pointer" : "text-gray-500"} w-1/2`}
                    onPress={() => {
                      if (!selected || selected?.id !== item.id) {
                        setSelected(item);
                      }
                      setFormFeedBack({ ...item, type: "VIEW" });
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="cursor-pointer text-white w-1/2"
                    isDisabled={!!isUpdateLoading}
                    onPress={() => {
                      mutate(getValues(), {
                        onError(err) {
                          const comment =
                            "body/comment must NOT have fewer than 1 characters";

                          if (err.message === comment) {
                            setUpdateError("Comment is required");

                            return toast.add(
                              {
                                description: "Comment is required",
                                title: "Feedback not updated",
                                type: "warning",
                              },
                              {
                                timeout: 5000,
                              },
                            );
                          }
                          toast.add(
                            {
                              description: err.message,
                              title: "Feedback not updated",
                              type: "error",
                            },
                            {
                              timeout: 5000,
                            },
                          );
                        },
                        onSuccess() {
                          setFormFeedBack({ ...item, type: "VIEW" });
                        },
                      });
                    }}
                    variant="primary"
                  >
                    {isUpdateLoading ? <Loading /> : "Save"}
                  </Button>
                </div>
              ) : !isDesignerOnly && !item.isResolved ? (
                <div className="flex w-full gap-2 mt-3">
                  <Button
                    className={`${isSelected ? "text-gray-700 border-gray-400 cursor-pointer" : "text-gray-500"} w-1/2`}
                    isDisabled={shouldDisableEditing}
                    onPress={() => {
                      setSelected(item);
                      setFormFeedBack({ ...item, type: "EDIT" });
                    }}
                    variant="secondary"
                  >
                    Edit
                  </Button>
                  <DeleteDialog
                    handleDelete={() => deleteFeedback()}
                    open={confirmDelete}
                    setOpen={setConfirmDelete}
                  />
                  <Button
                    className={`${isSelected ? "" : "opacity-50"} cursor pointer text-white bg-[#d92d20] w-1/2`}
                    isDisabled={shouldDisableEditing}
                    onPress={() => {
                      setSelected(item);
                      setConfirmDelete(true);
                    }}
                    variant="tertiary"
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
